import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, eur, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { FadeUp, Overline } from "../components/Reveal";

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", address: "", city: "", zip: "", country: "" });
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState(null);
  const [couponError, setCouponError] = useState("");

  useEffect(() => {
    if (user === false) {
      navigate("/auth?next=/checkout");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.name && !form.name) {
      setForm((f) => ({ ...f, name: user.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user) {
      api.get("/account/coupons").then((r) => setCoupons(r.data)).catch(() => {});
    }
  }, [user]);

  const now = Date.now();
  const isValidCoupon = (c) => !c.used && (!c.expires_at || new Date(c.expires_at).getTime() > now);
  const availableCoupons = coupons.filter(isValidCoupon);

  const applyCoupon = (codeRaw) => {
    const code = (codeRaw || "").trim().toUpperCase();
    setCouponError("");
    if (!code) return;
    const match = coupons.find((c) => c.code.toUpperCase() === code);
    if (!match) {
      setCouponError("Coupon not found");
      return;
    }
    if (!isValidCoupon(match)) {
      setCouponError(match.used ? "This coupon has already been used" : "This coupon has expired");
      return;
    }
    setApplied(match);
    setCouponInput("");
    toast.success(`${match.code} applied \u2014 ${match.discount_pct}% off`);
  };

  const removeCoupon = () => {
    setApplied(null);
    setCouponError("");
  };

  const discount = applied ? Math.round(subtotal * applied.discount_pct) / 100 : 0;
  const grandTotal = Math.max(0, Math.round((subtotal - discount) * 100) / 100);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, qty: i.qty })),
        shipping: form,
        coupon_code: applied ? applied.code : null,
      });
      clear();
      setPlaced(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not place order"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#141414] border border-white/10 px-5 py-4 text-sm text-[#F5F5F5] placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors duration-300";

  if (placed) {
    return (
      <div data-testid="order-success" className="min-h-screen pt-40 pb-28 px-6 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="w-16 h-16 mx-auto border border-gold flex items-center justify-center"
        >
          <Check size={28} className="text-gold" />
        </motion.div>
        <h1 className="mt-8 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5]">
          The Conquest is <span className="italic text-gold">Yours</span>
        </h1>
        <p className="mt-6 text-sm text-zinc-500">
          Order <span className="text-gold">{placed.id.slice(0, 8).toUpperCase()}</span> confirmed &mdash;{" "}
          {eur(placed.total)}
        </p>
        <div className="mt-10 flex justify-center gap-4 flex-wrap">
          <Link
            to="/account"
            data-testid="order-success-account-link"
            className="px-10 py-4 bg-gold text-black text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500"
          >
            View Orders
          </Link>
          <Link
            to="/shop"
            data-testid="order-success-shop-link"
            className="px-10 py-4 border border-white/20 text-zinc-300 text-xs tracking-[0.35em] uppercase hover:border-gold hover:text-gold transition-colors duration-500"
          >
            Continue
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid="checkout-empty" className="min-h-screen pt-40 pb-28 px-6 text-center">
        <p className="font-serif text-3xl text-zinc-400 italic">Your selection is empty</p>
        <Link
          to="/shop"
          className="mt-8 inline-block px-10 py-4 border border-gold text-gold text-xs tracking-[0.35em] uppercase hover:bg-gold hover:text-black transition-colors duration-500"
        >
          Explore Collections
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="checkout-page" className="min-h-screen pt-32 md:pt-40 pb-28">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <Overline>Final Step</Overline>
        <h1 className="mt-4 font-serif text-4xl md:text-6xl font-light text-[#F5F5F5]">Checkout</h1>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <FadeUp className="lg:col-span-3">
            <form onSubmit={placeOrder} className="flex flex-col gap-4" data-testid="checkout-form">
              <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-2">Shipping Address</p>
              <input data-testid="checkout-name" placeholder="Full name" value={form.name} onChange={set("name")} required className={inputCls} />
              <input data-testid="checkout-address" placeholder="Street address" value={form.address} onChange={set("address")} required className={inputCls} />
              <div className="grid grid-cols-2 gap-4">
                <input data-testid="checkout-city" placeholder="City" value={form.city} onChange={set("city")} required className={inputCls} />
                <input data-testid="checkout-zip" placeholder="Postal code" value={form.zip} onChange={set("zip")} required className={inputCls} />
              </div>
              <input data-testid="checkout-country" placeholder="Country" value={form.country} onChange={set("country")} required className={inputCls} />
              <button
                data-testid="place-order-button"
                type="submit"
                disabled={loading}
                className="lux-shimmer mt-6 py-4 bg-gold text-black text-xs tracking-[0.35em] uppercase font-semibold hover:bg-gold-hover transition-colors duration-500 disabled:opacity-50"
              >
                {loading ? "Placing..." : `Place Order \u2014 ${eur(grandTotal)}`}
              </button>
              <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 text-center">
                Demo checkout &mdash; no payment is processed
              </p>
            </form>
          </FadeUp>

          <FadeUp delay={0.1} className="lg:col-span-2">
            <div className="border border-white/10 bg-[#141414] p-8">
              <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-6">Your Selection</p>
              <div className="flex flex-col gap-5">
                {items.map((item) => (
                  <div key={item.key} className="flex gap-4 items-center">
                    <div className="w-14 h-16 bg-[#0A0A0A] border border-white/10 overflow-hidden flex-shrink-0">
                      {item.image && (
                        <img src={fileUrl(item.image)} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-serif text-base text-[#F5F5F5] leading-tight truncate">{item.name}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Size {item.size} &times; {item.qty}
                      </p>
                    </div>
                    <p className="text-sm text-gold">{eur(item.price * item.qty)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">Coupon</p>
                {applied ? (
                  <div data-testid="applied-coupon" className="flex items-center justify-between border border-gold/40 bg-gold/5 px-4 py-3">
                    <div>
                      <p className="text-sm text-gold tracking-[0.15em]">{applied.code}</p>
                      <p className="text-[10px] text-zinc-500 mt-0.5">{applied.discount_pct}% off applied</p>
                    </div>
                    <button
                      type="button"
                      data-testid="remove-coupon-button"
                      onClick={removeCoupon}
                      className="text-zinc-500 hover:text-red-400 transition-colors"
                      aria-label="Remove coupon"
                    >
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <input
                        data-testid="coupon-input"
                        value={couponInput}
                        onChange={(e) => { setCouponInput(e.target.value); setCouponError(""); }}
                        placeholder="Enter code"
                        className="flex-1 bg-[#0A0A0A] border border-white/10 px-4 py-3 text-sm text-[#F5F5F5] placeholder:text-zinc-600 focus:border-gold focus:outline-none transition-colors duration-300 uppercase tracking-[0.15em]"
                      />
                      <button
                        type="button"
                        data-testid="apply-coupon-button"
                        onClick={() => applyCoupon(couponInput)}
                        className="px-5 py-3 border border-gold text-gold text-[11px] tracking-[0.3em] uppercase hover:bg-gold hover:text-black transition-colors duration-300"
                      >
                        Apply
                      </button>
                    </div>
                    {couponError && (
                      <p data-testid="coupon-error" className="mt-2 text-[11px] text-red-400">{couponError}</p>
                    )}
                    {availableCoupons.length > 0 && (
                      <div className="mt-3 flex flex-col gap-2">
                        {availableCoupons.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            data-testid={`available-coupon-${c.code}`}
                            onClick={() => applyCoupon(c.code)}
                            className="flex items-center justify-between text-left border border-white/10 px-4 py-2.5 hover:border-gold/50 transition-colors duration-300 group"
                          >
                            <span>
                              <span className="text-xs text-gold tracking-[0.15em]">{c.code}</span>
                              <span className="block text-[10px] text-zinc-500 mt-0.5">{c.title}</span>
                            </span>
                            <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 group-hover:text-gold transition-colors">Use</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-2">
                <div className="flex justify-between items-baseline text-xs text-zinc-500">
                  <span className="tracking-[0.25em] uppercase">Subtotal</span>
                  <span data-testid="checkout-subtotal">{eur(subtotal)}</span>
                </div>
                {applied && (
                  <div className="flex justify-between items-baseline text-xs text-gold">
                    <span className="tracking-[0.25em] uppercase">Discount ({applied.discount_pct}%)</span>
                    <span data-testid="checkout-discount">&minus;{eur(discount)}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline mt-2">
                  <p className="text-xs tracking-[0.3em] uppercase text-zinc-500">Total</p>
                  <p data-testid="checkout-total" className="font-serif text-2xl text-gold">{eur(grandTotal)}</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
